/** Pictures of a scene drawn in the browser (docs/SPEC.md §6.1, §P28.2 (2)):
 *  every picture of a scene a person sees is drawn by the film player from
 *  the scene's bake, never by the server. `frameOf` mounts the player
 *  offscreen on the scene alone (`manifest.ts::sceneAlone`), draws local time
 *  `t` with the player's own `renderFrame`, and answers the canvas as an
 *  `ImageBitmap` of `size`. One frame is drawn at a time (one WebGL context
 *  more than the page's player, never several).
 *
 *  The captions are an iframe over the player's canvas: a tile is the
 *  picture alone, and a frame asked `captions: true` (the description's zip,
 *  §30.5) has them laid over it — the overlay template answers its layout as
 *  a self-contained SVG (`useOverlayFrame.picture`), drawn on the canvas's
 *  picture at the same size.
 *
 *  `zipOfFrames(manifest)` is the description's zip (P28-T10): one PNG per
 *  scene at its opening moment, its captions in, named as the description's
 *  footer names them (`frameName`, the server's `describe.frame_name`).
 *
 *  `target` frames one object alone (P28-T16, a stand-in's picture in the
 *  cast panel): the moment drawn once by the film's camera, then the view
 *  turned onto that object's box (`FilmPlayerHandle.frameOn`,
 *  `look.ts::framingOffset`) and drawn again; a scene without that object is
 *  an error, never the whole stage.
 *
 *  The player's code is loaded on the first frame (`lazy.tsx`): three stays
 *  out of the main bundle. `options.wrap` gives the player what a page's
 *  tests give theirs (a recording renderer, the overlay's stand-in). */
import { zipSync } from "fflate";
import { createElement, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import type { Aspect } from "../api/types";

import { loadFilmPlayer } from "./lazy";
import { type Caption, type Manifest, sceneAlone } from "./manifest";
import type { FilmPlayerHandle } from "./Player";

export interface FrameSize {
  width: number;
  height: number;
}

export interface FrameOptions {
  /** Stop an offscreen frame promptly when its download is cancelled. */
  signal?: AbortSignal;
  /** Wraps the offscreen player (the film's environment, in tests). */
  wrap?: (node: ReactNode) => ReactNode;
  /** How long a frame may take, ms. */
  timeoutMs?: number;
  /** Lay the scene's captions over the picture (they are not in a tile). */
  captions?: boolean;
  /** The object to frame alone, by its id in the scene (a cast member's). */
  target?: string;
}

/** A frame given up on after this long. */
export const FRAME_TIMEOUT_MS = 20_000;
/** How long a frame with captions waits for the captions' template. */
const CAPTIONS_READY_MS = 10_000;
/** The short side of the description's pictures (§22's stills had 540). */
export const ZIP_SHORT_SIDE = 540;

/** The quality level a frame is drawn at (`quality.ts`): one below the top,
 *  a tile is small. */
const FRAME_LEVEL = 2;

/** Past a caption's entrance, so that it is wholly in. */
const SETTLED_S = 0.05;
/** A caption's entrance when the template's own is not known here. */
const ENTRANCE_S = 0.5;
/** A scene without captions: a moment into it, when its objects are in. */
const NO_CAPTION_S = 1.5;

/** The moment a scene's picture shows (§22's rule, as the stills had it,
 *  without the objects' entrances, which the manifest does not carry): its
 *  first caption in, never past that caption's middle; without a caption, a
 *  moment into the scene. Local seconds. */
export function openingMoment(scene: {
  seconds: number;
  captions: readonly Pick<Caption, "t0" | "t1">[];
}): number {
  const last = Math.max(0, scene.seconds - 1e-3);
  const first = [...scene.captions].sort((a, b) => a.t0 - b.t0)[0];
  if (!first) return Math.min(NO_CAPTION_S, scene.seconds / 2, last);
  const at = Math.min(
    first.t0 + ENTRANCE_S + SETTLED_S,
    (first.t0 + first.t1) / 2,
  );
  return Math.min(Math.max(0, at), last);
}

let queue: Promise<unknown> = Promise.resolve();

/** The picture of scene `sceneId` of `manifest` at its local time `t`,
 *  `size` pixels, drawn by the player offscreen. Rejects when the scene is
 *  not baked yet, cannot be loaded, or the frame takes longer than the
 *  timeout. */
export function frameOf(
  manifest: Manifest,
  sceneId: string,
  t: number,
  size: FrameSize,
  options: FrameOptions = {},
): Promise<ImageBitmap> {
  const run = () => draw(manifest, sceneId, t, size, options);
  const next = queue.then(run, run);
  queue = next.catch(() => undefined);
  return next;
}

async function draw(
  manifest: Manifest,
  sceneId: string,
  t: number,
  size: FrameSize,
  options: FrameOptions,
): Promise<ImageBitmap> {
  options.signal?.throwIfAborted();
  const alone = sceneAlone(manifest, sceneId);
  if (!alone.scenes[0].ready) throw new Error(`scene ${sceneId} is not built`);
  const { FilmPlayer } = await loadFilmPlayer();
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.dataset.testid = "frame-player";
  Object.assign(host.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: `${size.width}px`,
    pointerEvents: "none",
  });
  document.body.appendChild(host);
  const root = createRoot(host);
  let timer = 0;
  let abortFrame: (() => void) | undefined;
  const deadline = new Promise<never>((_, reject) => {
    abortFrame = () =>
      reject(new DOMException("Frame cancelled", "AbortError"));
    options.signal?.addEventListener("abort", abortFrame, { once: true });
    timer = window.setTimeout(
      () => reject(new Error("frame timed out")),
      options.timeoutMs ?? FRAME_TIMEOUT_MS,
    );
  });
  let drawn = false;
  try {
    const mounted = new Promise<FilmPlayerHandle>((resolve) => {
      const player = createElement(FilmPlayer, {
        manifest: alone,
        controls: false,
        level: FRAME_LEVEL,
        // the picture at the size asked for, however tall the window is
        maxHeight: `${size.height}px`,
        onFrame: () => {
          drawn = true;
        },
        ref: (h: FilmPlayerHandle | null) => {
          if (h) resolve(h);
        },
      });
      root.render(options.wrap ? options.wrap(player) : player);
    });
    const handle = await Promise.race([mounted, deadline]);
    if (options.captions) {
      // the template must be there before the moment is drawn: the captions
      // are laid out by the draw that follows
      await Promise.race([
        handle.captionsReady(),
        new Promise((resolve) => window.setTimeout(resolve, CAPTIONS_READY_MS)),
        deadline,
      ]);
    }
    // the handle is there at once, the renderer only after the player's
    // effect: asked again until the moment is drawn (a scene whose bake
    // cannot be loaded never is)
    const moment = async () => {
      for (let tries = 0; ; tries += 1) {
        await new Promise((resolve) =>
          window.setTimeout(resolve, tries ? 50 : 0),
        );
        drawn = false;
        await Promise.race([handle.renderFrame(t), deadline]);
        if (drawn) return;
        if (tries >= 20) throw new Error(`scene ${sceneId} could not be drawn`);
      }
    };
    await moment();
    if (options.target !== undefined) {
      if (!handle.frameOn(options.target))
        throw new Error(`scene ${sceneId} has no ${options.target}`);
      await moment();
    }
    const canvas = host.querySelector("canvas");
    if (!canvas) throw new Error("the player drew no canvas");
    const svg = options.captions
      ? await Promise.race([handle.captionsPicture(), deadline])
      : null;
    if (svg) return await withCaptions(canvas, svg, size);
    return await createImageBitmap(canvas, {
      resizeWidth: size.width,
      resizeHeight: size.height,
      resizeQuality: "high",
    });
  } finally {
    window.clearTimeout(timer);
    if (abortFrame) options.signal?.removeEventListener("abort", abortFrame);
    const canvas = host.querySelector("canvas");
    root.unmount();
    // the context let go at once, not when it is collected: a list frames
    // scene after scene, and a browser keeps only a few contexts alive
    if (canvas && typeof WebGL2RenderingContext !== "undefined") {
      canvas
        .getContext("webgl2")
        ?.getExtension("WEBGL_lose_context")
        ?.loseContext();
    }
    host.remove();
  }
}

/** A frame as a picture's address (a JPEG data URL), for an `<img>`. */
export function frameUrl(bitmap: ImageBitmap): string {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.85);
}

/** The canvas's picture with the captions' SVG laid over it, at `size`. */
async function withCaptions(
  canvas: HTMLCanvasElement,
  svg: string,
  size: FrameSize,
): Promise<ImageBitmap> {
  const out = document.createElement("canvas");
  out.width = size.width;
  out.height = size.height;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("no 2D context");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(canvas, 0, 0, size.width, size.height);
  const picture = new Image();
  picture.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  await picture.decode();
  ctx.drawImage(picture, 0, 0, size.width, size.height);
  return createImageBitmap(out);
}

/** A frame as PNG bytes. */
export async function framePng(bitmap: ImageBitmap): Promise<Uint8Array> {
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
  if (!blob) throw new Error("the frame could not be encoded");
  return new Uint8Array(await blob.arrayBuffer());
}

/** The size of a frame of `aspect` whose short side is `short` pixels. */
export function frameSize(aspect: Aspect, short = ZIP_SHORT_SIDE): FrameSize {
  const [w, h] = aspect.split(":").map(Number);
  const k = short / Math.min(w, h);
  return { width: Math.round(w * k), height: Math.round(h * k) };
}

/** The file name of scene `index` (0-based)'s picture in the zip, as the
 *  description's footer names it (`describe.frame_name`):
 *  `01-s1-on-the-plinth.png`. */
export function frameName(
  index: number,
  scene: { id: string; name: string },
): string {
  const slug =
    scene.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "scene";
  return `${String(index + 1).padStart(2, "0")}-${scene.id}-${slug}.png`;
}

export interface ZipOptions extends Pick<FrameOptions, "wrap" | "timeoutMs"> {
  /** The short side of each picture, pixels. */
  short?: number;
  /** Draws one frame (`frameOf` by default; a test gives its own). */
  frame?: typeof frameOf;
  /** Encodes one frame (`framePng` by default). */
  png?: (bitmap: ImageBitmap) => Promise<Uint8Array>;
}

export interface FramesZip {
  zip: Blob;
  /** The scenes that could not be drawn (not built, or failed): left out. */
  missing: string[];
}

/** The description's pictures (§30.5): each scene of `manifest` at its
 *  opening moment, its captions in, one PNG per scene named by `frameName`,
 *  zipped in the browser. A scene that cannot be drawn is left out and
 *  named in `missing`; none drawn is an error. */
export async function zipOfFrames(
  manifest: Manifest,
  options: ZipOptions = {},
): Promise<FramesZip> {
  const draw = options.frame ?? frameOf;
  const encode = options.png ?? framePng;
  const size = frameSize(manifest.aspect, options.short);
  const files: Record<string, Uint8Array> = {};
  const missing: string[] = [];
  for (const [k, scene] of manifest.scenes.entries()) {
    try {
      const bitmap = await draw(
        manifest,
        scene.id,
        openingMoment(scene),
        size,
        {
          wrap: options.wrap,
          timeoutMs: options.timeoutMs,
          captions: true,
        },
      );
      files[frameName(k, scene)] = await encode(bitmap);
      bitmap.close?.();
    } catch {
      missing.push(scene.id);
    }
  }
  if (Object.keys(files).length === 0)
    throw new Error("no scene could be drawn");
  // PNGs are compressed already: stored, not deflated again
  const bytes = zipSync(files, { level: 0, mtime: new Date(1980, 0, 1) });
  return {
    zip: new Blob([bytes], { type: "application/zip" }),
    missing,
  };
}
